import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { accessProfilesAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { organizationOption } from '../../lib/selectOptions';
import { MODULE_GROUPS, MODULE_HELP, moduleLabel } from '../../lib/modules';
import { messageErreur } from '../../lib/erreurs';
import { Aide } from '@/components/ui/aide';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { SearchableSelect } from '../ui/searchable-select';

// Les trois niveaux d'accès d'un module.
const NIVEAUX = [
  { value: 'none', label: 'Aucun accès' },
  { value: 'read', label: 'Consulter' },
  { value: 'write', label: 'Modifier' },
];

const FORMULAIRE_VIDE = {
  name: '',
  levels: {},
  can_see_money: false,
  login_method: 'email',
};

export const pluriel = (nombre, mot) => `${nombre} ${mot}${nombre > 1 ? 's' : ''}`;

const levelsDepuisProfil = (profil) =>
  Object.fromEntries((profil?.modules || []).map((module) => [module.code, module.write ? 'write' : 'read']));

const formulaireDepuis = (profil) =>
  profil
    ? {
        name: profil.name || '',
        levels: levelsDepuisProfil(profil),
        can_see_money: Boolean(profil.can_see_money),
        login_method: profil.login_method === 'code' ? 'code' : 'email',
        reset_action_limits: false,
      }
    : FORMULAIRE_VIDE;

const FormulaireProfil = ({ editing, organizationId, organizations, onOpenChange, onSaved }) => {
  const { isSuperAdmin, enabledModules } = useAuth();
  const [form, setForm] = useState(() => formulaireDepuis(editing));
  const [societe, setSociete] = useState(editing?.organization_id || organizationId || '');
  const [saving, setSaving] = useState(false);

  // Un administrateur distribue ce que SA licence couvre (même source que le
  // sélecteur de modules des utilisateurs). Le super admin n'a pas de licence
  // en contexte: tout est proposé, le serveur ramène à la licence de la société.
  const groupes = useMemo(() => {
    const licencies = isSuperAdmin || !enabledModules ? null : new Set(enabledModules);
    const deja = new Set();
    return MODULE_GROUPS.map((groupe) => ({
      label: groupe.label,
      codes: groupe.modules
        .map(([code]) => code)
        .filter((code) => {
          if (deja.has(code)) return false;
          if (licencies && !licencies.has(code)) return false;
          deja.add(code);
          return true;
        }),
    })).filter((groupe) => groupe.codes.length > 0);
  }, [isSuperAdmin, enabledModules]);

  const organizationOptions = useMemo(() => (organizations || []).map(organizationOption), [organizations]);
  const societeChoisie = isSuperAdmin ? societe : undefined;

  const choisirNiveau = (code, niveau) =>
    setForm((precedent) => ({ ...precedent, levels: { ...precedent.levels, [code]: niveau } }));

  const nombreModules = Object.values(form.levels).filter((niveau) => niveau && niveau !== 'none').length;

  const enregistrer = async (event) => {
    event.preventDefault();
    const modules = Object.entries(form.levels)
      .filter(([, niveau]) => niveau && niveau !== 'none')
      .map(([code, niveau]) => ({ code, write: niveau === 'write' }));
    if (modules.length === 0) {
      toast.error('Choisissez au moins un module.');
      return;
    }
    if (isSuperAdmin && !societeChoisie) {
      toast.error('Choisissez la société.');
      return;
    }
    const body = {
      name: form.name.trim(),
      modules,
      can_see_money: form.can_see_money,
      login_method: form.login_method,
      ...(editing?.has_action_limits ? { reset_action_limits: Boolean(form.reset_action_limits) } : {}),
    };
    try {
      setSaving(true);
      let resultat;
      if (editing) {
        resultat = await accessProfilesAPI.update(editing.id, body, societeChoisie);
        toast.success('Profil mis à jour');
      } else {
        resultat = await accessProfilesAPI.create(body, societeChoisie);
        toast.success('Profil créé');
      }
      onOpenChange(false);
      onSaved?.(resultat, societeChoisie || null, Boolean(editing));
    } catch (error) {
      toast.error(messageErreur(error, "Impossible d'enregistrer le profil"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={enregistrer} className="space-y-4">
      {isSuperAdmin && !editing && (
        <div>
          <label className="mb-1 block text-sm font-medium">Société</label>
          <SearchableSelect
            value={societe}
            onValueChange={setSociete}
            aria-label="Société du profil"
            placeholder="Choisir la société"
            searchPlaceholder="Rechercher une société…"
            emptyMessage="Aucune société trouvée"
            options={organizationOptions}
            className="h-10"
            required
          />
        </div>
      )}
      <div>
        <label htmlFor="profil-nom" className="mb-1 block text-sm font-medium">
          Nom du profil
        </label>
        <Input
          id="profil-nom"
          value={form.name}
          onChange={(event) => setForm({ ...form, name: event.target.value })}
          required
          minLength={2}
          maxLength={80}
        />
      </div>

      {editing?.has_action_limits && (
        <label className="flex items-center justify-between gap-3 rounded-lg border border-border p-3 text-sm">
          <span>
            Utiliser les droits standards{' '}
            <Aide titre="Restrictions reprises">
              Les actions historiques restent limitées par défaut. Activer ce choix les remplace par les niveaux
              sélectionnés pour {editing.users_count || 0} compte(s).
            </Aide>
          </span>
          <Switch
            checked={Boolean(form.reset_action_limits)}
            onCheckedChange={(value) => setForm({ ...form, reset_action_limits: value })}
            aria-label="Utiliser les droits standards"
          />
        </label>
      )}
      <div className="space-y-3">
        {groupes.map((groupe) => (
          <fieldset key={groupe.label} className="rounded-lg border border-border p-3">
            <legend className="px-1 text-sm font-medium">{groupe.label}</legend>
            <div className="space-y-1.5">
              {groupe.codes.map((code) => {
                const niveau = form.levels[code] || 'none';
                return (
                  <div
                    key={code}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border/60 p-2"
                  >
                    <span className="text-sm font-medium text-foreground" title={MODULE_HELP[code] || undefined}>
                      {moduleLabel(code)}
                    </span>
                    <div
                      role="radiogroup"
                      aria-label={moduleLabel(code)}
                      className="inline-flex overflow-hidden rounded-md border border-border text-xs"
                    >
                      {NIVEAUX.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          role="radio"
                          aria-checked={niveau === option.value}
                          aria-label={`${moduleLabel(code)} : ${option.label}`}
                          onClick={() => choisirNiveau(code, option.value)}
                          className={`px-2.5 py-1 font-medium transition-colors ${
                            niveau === option.value
                              ? option.value === 'none'
                                ? 'bg-muted text-foreground'
                                : 'bg-primary text-primary-foreground'
                              : 'bg-card text-muted-foreground hover:bg-muted/50'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </fieldset>
        ))}
        <p className="text-xs text-muted-foreground">
          {pluriel(nombreModules, 'module')} choisi{nombreModules > 1 ? 's' : ''}
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="flex items-center justify-between gap-3 rounded-lg border border-border p-3 text-sm font-medium">
          Montants visibles
          <Switch
            checked={form.can_see_money}
            onCheckedChange={(valeur) => setForm({ ...form, can_see_money: valeur })}
            aria-label="Montants visibles"
          />
        </label>
        <label className="flex items-center justify-between gap-3 rounded-lg border border-border p-3 text-sm font-medium">
          Connexion par code (application mobile / comptoir)
          <Switch
            checked={form.login_method === 'code'}
            onCheckedChange={(valeur) => setForm({ ...form, login_method: valeur ? 'code' : 'email' })}
            aria-label="Connexion par code"
          />
        </label>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
          Annuler
        </Button>
        <Button type="submit" disabled={saving}>
          {editing ? 'Enregistrer' : 'Créer le profil'}
        </Button>
      </div>
    </form>
  );
};

/**
 * Création et modification d'un profil d'accès, utilisées par l'onglet
 * « Profils d'accès » et par le formulaire d'un compte (« Créer un profil »).
 * `onSaved(profil, idSociete, modification)` est appelé après l'enregistrement.
 */
export const ProfileDialog = ({ open, onOpenChange, editing, organizationId, organizations, onSaved }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-1">
          {editing ? 'Modifier le profil' : 'Nouveau profil'}
          <Aide titre="Profils d'accès">
            Un profil ne dépasse jamais la licence de la société. La gestion des comptes, des profils et des
            réglages reste réservée à l’administrateur.
          </Aide>
        </DialogTitle>
      </DialogHeader>
      <FormulaireProfil
        editing={editing}
        organizationId={organizationId}
        organizations={organizations}
        onOpenChange={onOpenChange}
        onSaved={onSaved}
      />
    </DialogContent>
  </Dialog>
);

export default ProfileDialog;
